import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Folder, FolderOpen, Loader2, Plus, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { HookEditRow, HookRow } from './HookRows';
import { useAuthStore } from '../store';
import { adHooksAPI } from '../lib/api';

const COLS = ['Gancho', 'Tipo', 'Nicho', 'Ejemplo', 'Usado en', 'Resultado', 'Notas', 'Fecha añadido', ''];
const SORTS = { fecha_desc: 'Más recientes', fecha_asc: 'Más antiguos', tipo: 'Por tipo' };

export default function AdHooksPage() {
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [tipo, setTipo] = useState('');
  const [nicho, setNicho] = useState('');
  const [sort, setSort] = useState('fecha_desc');
  const [editing, setEditing] = useState(null);
  const [coleccion, setColeccion] = useState('');
  const load = useCallback(() => adHooksAPI.list().then(setData).catch((e) => toast.error(e.message)), []);
  useEffect(() => { if (user?.role === 'ceo') load(); }, [load, user?.role]);
  useEffect(() => { setEditing(null); }, [coleccion]);

  const colecciones = useMemo(() => {
    const c = {};
    (data?.rows || []).forEach((r) => { if (r.coleccion) c[r.coleccion] = (c[r.coleccion] || 0) + 1; });
    return Object.entries(c);
  }, [data]);
  const base = useMemo(() => (data?.rows || []).filter((r) => (r.coleccion || '') === coleccion), [data, coleccion]);
  const nichos = useMemo(() => [...new Set(base.map((r) => r.nicho).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')), [base]);
  const match = useCallback((r) => {
    const s = q.trim().toLowerCase();
    return (!tipo || (tipo === '__none' ? !r.tipo : r.tipo === tipo)) && (!nicho || r.nicho === nicho) && (!s || (r.gancho || '').toLowerCase().includes(s));
  }, [q, tipo, nicho]);
  const elsewhere = useMemo(() => ((q.trim() || tipo || nicho) ? (data?.rows || []).filter((r) => (r.coleccion || '') !== coleccion && match(r)).length : 0), [data, coleccion, match, q, tipo, nicho]);
  const rows = useMemo(() => {
    const out = base.filter(match);
    const byDate = (a, b) => (a.created_at || '').localeCompare(b.created_at || '');
    if (sort === 'fecha_asc') return out.sort(byDate);
    if (sort === 'tipo') return out.sort((a, b) => (!a.tipo) - (!b.tipo) || (a.tipo || '').localeCompare(b.tipo || '', 'es') || (a.gancho ? 0 : 1) - (b.gancho ? 0 : 1));
    return out.sort((a, b) => byDate(b, a));
  }, [base, match, sort]);

  if (user && user.role !== 'ceo') return <Navigate to="/resources" replace />;

  const addType = async (n) => { await adHooksAPI.addType(n); setData((d) => ({ ...d, types: [...new Set([...d.types, n])].sort((a, b) => a.localeCompare(b, 'es')) })); };
  const create = async (f) => {
    try { const r = await adHooksAPI.create({ ...f, coleccion }); setData((d) => ({ ...d, rows: [r, ...d.rows] })); setEditing(null); toast.success('Gancho añadido'); }
    catch (e) { toast.error(e.message); }
  };
  const update = async (id, f) => {
    try { const r = await adHooksAPI.update(id, f); setData((d) => ({ ...d, rows: d.rows.map((x) => (x.id === id ? r : x)) })); setEditing(null); toast.success('Guardado'); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (h) => {
    if (!window.confirm(`¿Eliminar el gancho${h.gancho ? ` «${h.gancho.slice(0, 60)}»` : ''}?`)) return;
    try { await adHooksAPI.remove(h.id); setData((d) => ({ ...d, rows: d.rows.filter((x) => x.id !== h.id) })); toast.success('Gancho eliminado'); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="ad-hooks-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/resources?tab=direction" className="text-muted-foreground hover:text-foreground" data-testid="ad-hooks-back"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold flex items-center gap-2"><Sparkles className="w-7 h-7 text-primary" />Ganchos ganadores</h1>
          <p className="text-sm text-muted-foreground">Biblioteca de ganchos para anuncios · Recursos de dirección (solo CEOs)</p>
        </div>
        <Button onClick={() => setEditing('new')} disabled={editing === 'new'} data-testid="ad-hooks-add-btn"><Plus className="w-4 h-4 mr-1" />Añadir gancho</Button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en el texto del gancho…" className="pl-9" data-testid="ad-hooks-search" />
        </div>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="h-10 rounded-md border px-2 text-sm bg-background" data-testid="ad-hooks-filter-tipo">
          <option value="">Todos los tipos</option><option value="__none">Sin tipo</option>
          {(data?.types || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={nicho} onChange={(e) => setNicho(e.target.value)} className="h-10 rounded-md border px-2 text-sm bg-background" data-testid="ad-hooks-filter-nicho">
          <option value="">Todos los nichos</option>
          {nichos.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-md border px-2 text-sm bg-background" data-testid="ad-hooks-sort">
          {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {data && <span className="text-xs text-muted-foreground" data-testid="ad-hooks-count">{rows.length} de {base.length}</span>}
      </div>
      {coleccion && (
        <div className="flex items-center gap-2 text-sm" data-testid="ad-hooks-breadcrumb">
          <button onClick={() => setColeccion('')} className="text-primary hover:underline inline-flex items-center gap-1" data-testid="ad-hooks-back-main"><ArrowLeft className="w-4 h-4" />Nuestros ganchos</button>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold inline-flex items-center gap-1"><FolderOpen className="w-4 h-4 text-amber-500" />{coleccion}</span>
        </div>
      )}
      {elsewhere > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="ad-hooks-elsewhere">
          {coleccion ? <>También hay {elsewhere} resultado{elsewhere > 1 ? 's' : ''} en <button className="text-primary hover:underline" onClick={() => setColeccion('')}>Nuestros ganchos</button>.</>
            : <>También hay {elsewhere} resultado{elsewhere > 1 ? 's' : ''} dentro de {colecciones.map(([c], i) => <button key={c} className="text-primary hover:underline" onClick={() => setColeccion(c)}>{i ? ', ' : ''}{c}</button>)}.</>}
        </p>
      )}
      {!data ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <table className="w-full text-left" data-testid="ad-hooks-table">
            <thead className="bg-muted/50 sticky top-0">
              <tr>{COLS.map((c) => <th key={c} className="p-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold whitespace-nowrap">{c}</th>)}</tr>
            </thead>
            <tbody>
              {editing === 'new' && <HookEditRow initial={{ tipo: tipo && tipo !== '__none' ? tipo : '', nicho }} types={data.types} onAddType={addType} onSave={create} onCancel={() => setEditing(null)} testId="hook-new" />}
              {!coleccion && colecciones.map(([c, n]) => (
                <tr key={c} className="border-b bg-amber-50/60 hover:bg-amber-50 cursor-pointer" onClick={() => setColeccion(c)} data-testid={`hook-collection-${c}`}>
                  <td className="p-3" colSpan={COLS.length - 1}>
                    <span className="inline-flex items-center gap-2 font-semibold text-sm"><Folder className="w-5 h-5 text-amber-500" />{c}</span>
                    <span className="ml-3 text-xs text-muted-foreground">{n} ganchos · PDF original en Recursos de dirección → Ads</span>
                  </td>
                  <td className="p-3 text-right"><span className="text-xs font-medium text-primary whitespace-nowrap">Abrir →</span></td>
                </tr>
              ))}
              {rows.map((h) => (editing === h.id
                ? <HookEditRow key={h.id} initial={h} types={data.types} onAddType={addType} onSave={(f) => update(h.id, f)} onCancel={() => setEditing(null)} testId={`hook-edit-row-${h.id}`} />
                : <HookRow key={h.id} h={h} onEdit={() => setEditing(h.id)} onDelete={() => remove(h)} />))}
              {!rows.length && editing !== 'new' && (coleccion || !colecciones.length) && <tr><td colSpan={COLS.length} className="p-10 text-center text-sm text-muted-foreground">No hay ganchos con estos filtros.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
